/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { useState } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  EdgeProps,
} from '@xyflow/react';
import { PortRule } from './types';
import { ElkPoint } from './elkLayout';

export type IngressEdgeData = {
  ports: PortRule[] | null;
  portsDiffer: boolean;
  // env chips shown only when the rule doesn't cover every callee env.
  envs: string[];
  partial: boolean;
  staged?: 'add' | 'remove';
  // ELK-routed orthogonal polyline (absolute coords). Nodes are not
  // draggable, so the route is always current; the straight-line fallback
  // below only covers the frame before a route exists.
  points?: ElkPoint[];
};

const portsLabel = (ports: PortRule[] | null, portsDiffer: boolean): string => {
  if (portsDiffer) return 'ports vary by env';
  if (!ports || ports.length === 0) return 'all ports';
  return ports.map(p => `${p.port}${p.protocol === 'UDP' ? '/udp' : ''}`).join(', ');
};

/** True when the label would just repeat the default (all ports ± partial envs). */
const isDefaultPortsLabel = (data: IngressEdgeData): boolean =>
  !data.portsDiffer && (!data.ports || data.ports.length === 0) && !data.staged;

const EDGE_COLORS = {
  add: '#4caf50',
  remove: '#f44336',
  partial: '#ff9800',
  default: '#78909c',
} as const;

/** Rounded-corner SVG path through an orthogonal polyline. */
const roundedPathFromPoints = (pts: ElkPoint[], radius = 8): string => {
  if (pts.length < 2) return '';
  const seg = (a: ElkPoint, b: ElkPoint) => Math.hypot(b.x - a.x, b.y - a.y);
  const toward = (from: ElkPoint, to: ElkPoint, dist: number): ElkPoint => {
    const len = seg(from, to) || 1;
    return {
      x: from.x + ((to.x - from.x) / len) * dist,
      y: from.y + ((to.y - from.y) / len) * dist,
    };
  };
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length - 1; i += 1) {
    const r = Math.min(
      radius,
      seg(pts[i - 1], pts[i]) / 2,
      seg(pts[i], pts[i + 1]) / 2,
    );
    const enter = toward(pts[i], pts[i - 1], r);
    const exit = toward(pts[i], pts[i + 1], r);
    d += ` L ${enter.x} ${enter.y} Q ${pts[i].x} ${pts[i].y} ${exit.x} ${exit.y}`;
  }
  const last = pts[pts.length - 1];
  return `${d} L ${last.x} ${last.y}`;
};

/** Label anchor: midpoint of the longest polyline segment. */
const longestSegmentMid = (pts: ElkPoint[]): [number, number] => {
  let best = 0;
  let bestLen = -1;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const len = Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
    if (len > bestLen) {
      bestLen = len;
      best = i;
    }
  }
  return [
    (pts[best].x + pts[best + 1].x) / 2,
    (pts[best].y + pts[best + 1].y) / 2,
  ];
};

export const IngressFlowEdge = (props: EdgeProps) => {
  const data = (props.data ?? {}) as IngressEdgeData;
  const [hovered, setHovered] = useState(false);
  const pts =
    data.points && data.points.length >= 2
      ? data.points
      : // No route yet (a frame between a structural change and the next
        // ELK pass): a straight line keeps the connection visible.
        [
          { x: props.sourceX, y: props.sourceY },
          { x: props.targetX, y: props.targetY },
        ];
  const path = roundedPathFromPoints(pts);
  const [labelX, labelY] = longestSegmentMid(pts);

  const color =
    EDGE_COLORS[data.staged ?? (data.partial ? 'partial' : 'default')];

  // Default "all ports" is the main clutter -- reserve it for hover. But
  // WHICH envs a partial edge covers is real information (orange dashes only
  // say "partial"), so the env annotation stays always-on.
  const showLabel = !isDefaultPortsLabel(data) || data.partial || hovered;

  const labelText = [
    !isDefaultPortsLabel(data) || hovered
      ? portsLabel(data.ports, data.portsDiffer)
      : null,
    data.partial && data.envs.length > 0 ? data.envs.join(', ') : null,
    data.staged === 'add' ? 'staged' : null,
    data.staged === 'remove' ? 'removing' : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      {/* Wider invisible hit path so hover works on thin strokes. */}
      <path
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ cursor: 'default' }}
      />
      <BaseEdge
        id={props.id}
        path={path}
        markerEnd={props.markerEnd}
        style={{
          stroke: color,
          strokeWidth: (() => {
            if (data.staged) return 2.5;
            return hovered ? 2 : 1.5;
          })(),
          strokeDasharray: data.staged || data.partial ? '6 4' : undefined,
          pointerEvents: 'none',
        }}
      />
      {showLabel && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              fontSize: 9,
              background: 'rgba(255,255,255,0.9)',
              color: '#37474f',
              borderRadius: 3,
              padding: '0 4px',
              border: `1px solid ${color}`,
              pointerEvents: 'none',
              textDecoration:
                data.staged === 'remove' ? 'line-through' : undefined,
              whiteSpace: 'nowrap',
            }}
          >
            {labelText}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};
